import React, { useState, useEffect, useCallback } from 'react';
import { tiposAPI, expedientesAPI } from '../../services/api';
import '../../styles/dashboard-avanzado.css';

const FiltrosAvanzados = ({ onFilterChange, filtrosActuales }) => {
  const [tipos, setTipos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [showPeriodRange, setShowPeriodRange] = useState(false);

  const [filtros, setFiltros] = useState(() => {
    const n = new Date();
    return {
      fechaDesde: '',
      fechaHasta: '',
      meses: [String(n.getMonth() + 1)],
      anio: String(n.getFullYear()),
      tipos: [],
      clienteId: '',
      montoMin: '',
      montoMax: '',
      estadoPago: [],
      estadoExpediente: []
    };
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (filtrosActuales) {
      setFiltros((prev) => ({ ...prev, ...filtrosActuales }));
    }
  }, [filtrosActuales]);

  useEffect(() => {
    if (!filtrosActuales) return;
    if (filtrosActuales.fechaDesde || filtrosActuales.fechaHasta) {
      setShowPeriodRange(true);
    } else if ((filtrosActuales.meses?.length || 0) > 0) {
      setShowPeriodRange(false);
    } else {
      setShowPeriodRange(false);
    }
  }, [filtrosActuales]);

  const loadData = async () => {
    try {
      const [tiposRes, clientesRes] = await Promise.all([
        tiposAPI.getTipos(),
        expedientesAPI.getClientes()
      ]);
      setTipos(tiposRes.data);
      setClientes(clientesRes.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
    }
  };

  const handleChange = useCallback((field, value) => {
    setFiltros((prev) => {
      let nuevosFiltros = { ...prev, [field]: value };
      if ((field === 'fechaDesde' || field === 'fechaHasta') && value) {
        nuevosFiltros.meses = [];
      }
      return nuevosFiltros;
    });
  }, []);

  const handleMultiSelectChange = useCallback((field, value) => {
    setFiltros((prev) => {
      const currentValues = prev[field] || [];
      let newValues;
      if (currentValues.includes(value)) {
        newValues = currentValues.filter((v) => v !== value);
      } else {
        newValues = [...currentValues, value];
      }
      const nuevosFiltros = { ...prev, [field]: newValues };
      if (field === 'meses' && newValues.length > 0) {
        nuevosFiltros.fechaDesde = '';
        nuevosFiltros.fechaHasta = '';
        if (!nuevosFiltros.anio) {
          nuevosFiltros.anio = new Date().getFullYear().toString();
        }
      }
      return nuevosFiltros;
    });
  }, []);

  const aplicarFiltros = () => {
    onFilterChange(filtros);
  };

  /** Igual que aplicar y además colapsa el panel “Más” */
  const aplicarYCerrarPanel = () => {
    onFilterChange(filtros);
    setMoreOpen(false);
  };

  const limpiarFiltros = () => {
    const filtrosLimpios = {
      fechaDesde: '',
      fechaHasta: '',
      meses: [],
      anio: '',
      tipos: [],
      clienteId: '',
      montoMin: '',
      montoMax: '',
      estadoPago: [],
      estadoExpediente: []
    };
    setFiltros(filtrosLimpios);
    onFilterChange(filtrosLimpios);
    setShowPeriodRange(false);
  };

  const contarFiltrosActivos = () => {
    let count = 0;
    if (filtros.fechaDesde || filtros.fechaHasta) count++;
    if (filtros.meses?.length > 0) count++;
    if (filtros.tipos?.length > 0) count++;
    if (filtros.clienteId) count++;
    if (filtros.montoMin || filtros.montoMax) count++;
    if (filtros.estadoPago?.length > 0) count++;
    if (filtros.estadoExpediente?.length > 0) count++;
    return count;
  };

  const mesesCfg = [
    { value: '1', label: 'Ene' },
    { value: '2', label: 'Feb' },
    { value: '3', label: 'Mar' },
    { value: '4', label: 'Abr' },
    { value: '5', label: 'May' },
    { value: '6', label: 'Jun' },
    { value: '7', label: 'Jul' },
    { value: '8', label: 'Ago' },
    { value: '9', label: 'Sep' },
    { value: '10', label: 'Oct' },
    { value: '11', label: 'Nov' },
    { value: '12', label: 'Dic' }
  ];

  const textoResumenCorto = () => {
    if (filtros.fechaDesde || filtros.fechaHasta) {
      const a = filtros.fechaDesde || '…';
      const b = filtros.fechaHasta || '…';
      return `${a} → ${b}`;
    }
    if (filtros.meses?.length > 0) {
      const nombres = filtros.meses
        .sort((a, b) => Number(a) - Number(b))
        .map((m) => mesesCfg.find((x) => x.value === m)?.label || m)
        .join(', ');
      return `${nombres} · ${filtros.anio || ''}`.trim();
    }
    return 'Sin período definido';
  };

  const aniosDisponibles = () => {
    const anioActual = new Date().getFullYear();
    const anios = [];
    for (let i = anioActual; i >= 2020; i--) {
      anios.push(i.toString());
    }
    return anios;
  };

  const quitarMeses = () => {
    handleChange('meses', []);
  };

  const tieneMontos = !!(filtros.montoMin || filtros.montoMax);
  const countDrawer =
    (filtros.tipos?.length || 0) +
    (filtros.clienteId ? 1 : 0) +
    (tieneMontos ? 1 : 0) +
    (filtros.estadoPago?.length || 0) +
    (filtros.estadoExpediente?.length || 0);

  return (
    <div className="da-filtros-wrap mb-3">
      <div className="da-filtros">
        <div className="da-filtros__head">
          <div className="da-filtros__title">
            <i className="fas fa-filter me-2 text-secondary" aria-hidden />
            <span>Filtros</span>
            {contarFiltrosActivos() > 0 && (
              <span className="badge rounded-pill bg-primary ms-2">{contarFiltrosActivos()}</span>
            )}
          </div>
          <div className="da-filtros__head-actions">
            <button type="button" className="btn btn-sm btn-primary" onClick={aplicarFiltros}>
              Aplicar
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={limpiarFiltros}
              title="Quitar período y filtros para ver todo el histórico"
            >
              Todo
            </button>
            <button
              type="button"
              className={`btn btn-sm da-filtros__more-toggle ${moreOpen ? 'active' : 'btn-outline-secondary'}`}
              onClick={() => setMoreOpen((v) => !v)}
              aria-expanded={moreOpen}
            >
              Más{' '}
              {countDrawer > 0 && (
                <span className="badge rounded-pill bg-secondary ms-1">{countDrawer}</span>
              )}
              <i className={`fas fa-chevron-${moreOpen ? 'up' : 'down'} ms-1 small`} aria-hidden />
            </button>
          </div>
        </div>

        <p className="da-filtros__hint mb-2">
          Por <strong>fecha de creación</strong> del expediente · <span className="text-muted">{textoResumenCorto()}</span>
        </p>

        <div className="da-filtros__period">
          <div className="da-filtros__seg" role="group" aria-label="Modo de período">
            <button
              type="button"
              className={!showPeriodRange ? 'is-active' : ''}
              onClick={() => {
                setShowPeriodRange(false);
                setFiltros((prev) => ({ ...prev, fechaDesde: '', fechaHasta: '' }));
              }}
            >
              Mes y año
            </button>
            <button
              type="button"
              className={showPeriodRange ? 'is-active' : ''}
              onClick={() => {
                setShowPeriodRange(true);
                setFiltros((prev) => ({ ...prev, meses: [] }));
              }}
            >
              Rango de fechas
            </button>
          </div>

          {!showPeriodRange && (
            <div className="da-filtros__meses-row">
              <label className="da-filtros__inline-label" htmlFor="da-filtro-anio">
                Año
              </label>
              <select
                id="da-filtro-anio"
                className="form-select form-select-sm da-filtros__year"
                value={filtros.anio}
                onChange={(e) => handleChange('anio', e.target.value)}
                disabled={(filtros.meses?.length || 0) === 0 || !!(filtros.fechaDesde || filtros.fechaHasta)}
              >
                <option value="">—</option>
                {aniosDisponibles().map((anio) => (
                  <option key={anio} value={anio}>
                    {anio}
                  </option>
                ))}
              </select>
              <div className="da-filtros__pill-scroll" aria-label="Meses">
                {mesesCfg.map((mes) => {
                  const locked = !!(filtros.fechaDesde || filtros.fechaHasta);
                  const on = filtros.meses?.includes(mes.value);
                  return (
                    <button
                      key={mes.value}
                      type="button"
                      className={`da-pill-mes ${on ? 'da-pill-mes--on' : ''}`}
                      disabled={locked}
                      aria-pressed={on}
                      onClick={() => handleMultiSelectChange('meses', mes.value)}
                    >
                      {mes.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {showPeriodRange && (
            <div className="da-filtros__range-row">
              <div>
                <label className="form-label small mb-0 text-muted">Desde</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filtros.fechaDesde}
                  onChange={(e) => handleChange('fechaDesde', e.target.value)}
                  disabled={!showPeriodRange || (filtros.meses?.length || 0) > 0}
                />
              </div>
              <div>
                <label className="form-label small mb-0 text-muted">Hasta</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filtros.fechaHasta}
                  onChange={(e) => handleChange('fechaHasta', e.target.value)}
                  disabled={!showPeriodRange || (filtros.meses?.length || 0) > 0}
                />
              </div>
            </div>
          )}
        </div>

        {moreOpen && (
          <div className="da-filtros__drawer">
            <div className="row g-2 align-items-start">
              <div className="col-12">
                <label className="form-label small fw-semibold mb-1">Tipos</label>
                <div className="da-filtros__tipos-grid" role="group" aria-label="Tipos de expediente">
                  {tipos.map((tipo) => (
                    <label key={tipo.id} className="da-check-row da-check-row--dense">
                      <input
                        type="checkbox"
                        checked={filtros.tipos?.includes(tipo.id.toString())}
                        onChange={() => handleMultiSelectChange('tipos', tipo.id.toString())}
                      />
                      <span>{tipo.nombre}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="col-md-7 col-xl-8">
                <label className="form-label small fw-semibold mb-1" htmlFor="da-filtro-cliente">
                  Cliente
                </label>
                <select
                  id="da-filtro-cliente"
                  className="form-select form-select-sm"
                  value={filtros.clienteId}
                  onChange={(e) => handleChange('clienteId', e.target.value)}
                >
                  <option value="">Todos</option>
                  {clientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-5 col-xl-4">
                <label className="form-label small fw-semibold mb-1">Monto contrato</label>
                <div className="d-flex gap-1 align-items-center">
                  <input
                    type="number"
                    className="form-control form-control-sm da-filtros__monto-inp"
                    placeholder="Mín"
                    value={filtros.montoMin}
                    onChange={(e) => handleChange('montoMin', e.target.value)}
                    step="0.01"
                  />
                  <span className="text-muted small px-0" aria-hidden>
                    –
                  </span>
                  <input
                    type="number"
                    className="form-control form-control-sm da-filtros__monto-inp"
                    placeholder="Máx"
                    value={filtros.montoMax}
                    onChange={(e) => handleChange('montoMax', e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="col-12">
                <label className="form-label small fw-semibold mb-1 text-muted">Estados</label>
                <div className="da-filtros__estados-tight">
                  <span className="da-filtros__est-badge da-filtros__est-muted">Pago</span>
                  {['PAGADO', 'PARCIAL', 'PENDIENTE'].map((estado) => (
                    <label key={estado} className="da-mini-tag da-mini-tag--xs">
                      <input
                        type="checkbox"
                        checked={filtros.estadoPago?.includes(estado)}
                        onChange={() => handleMultiSelectChange('estadoPago', estado)}
                      />
                      <span
                        className={
                          estado === 'PAGADO'
                            ? 'text-success'
                            : estado === 'PARCIAL'
                              ? 'text-warning'
                              : 'text-danger'
                        }
                      >
                        {estado}
                      </span>
                    </label>
                  ))}
                  <span className="da-filtros__sep" aria-hidden />
                  <span className="da-filtros__est-badge da-filtros__est-muted">Exped.</span>
                  {['ABIERTO', 'CERRADO', 'VENCIDO'].map((estado) => (
                    <label key={estado} className="da-mini-tag da-mini-tag--xs">
                      <input
                        type="checkbox"
                        checked={filtros.estadoExpediente?.includes(estado)}
                        onChange={() => handleMultiSelectChange('estadoExpediente', estado)}
                      />
                      <span>{estado}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="col-12 d-flex gap-2 pt-1">
                <button type="button" className="btn btn-sm btn-primary" onClick={aplicarYCerrarPanel}>
                  Aplicar y cerrar panel
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setMoreOpen(false)}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {contarFiltrosActivos() > 0 && (
          <div className="da-filtros__chips" aria-label="Filtros activos">
            {(filtros.meses?.length || 0) > 0 && (
              <button type="button" className="da-chip-rem" onClick={quitarMeses}>
                {filtros.meses
                  .map((m) => mesesCfg.find((x) => x.value === m)?.label || m)
                  .join(', ')}{' '}
                · {filtros.anio}
                <span className="da-chip-rem__x" aria-hidden>
                  ×
                </span>
              </button>
            )}
            {filtros.fechaDesde && (
              <button type="button" className="da-chip-rem" onClick={() => handleChange('fechaDesde', '')}>
                Desde {filtros.fechaDesde}
                <span className="da-chip-rem__x" aria-hidden>
                  ×
                </span>
              </button>
            )}
            {filtros.fechaHasta && (
              <button type="button" className="da-chip-rem" onClick={() => handleChange('fechaHasta', '')}>
                Hasta {filtros.fechaHasta}
                <span className="da-chip-rem__x" aria-hidden>
                  ×
                </span>
              </button>
            )}
            {filtros.tipos?.map((tipoId) => {
              const tipo = tipos.find((t) => t.id.toString() === tipoId);
              return tipo ? (
                <button
                  key={tipoId}
                  type="button"
                  className="da-chip-rem"
                  onClick={() => handleMultiSelectChange('tipos', tipoId)}
                >
                  {tipo.nombre}
                  <span className="da-chip-rem__x" aria-hidden>
                    ×
                  </span>
                </button>
              ) : null;
            })}
            {filtros.clienteId && (
              <button type="button" className="da-chip-rem" onClick={() => handleChange('clienteId', '')}>
                {clientes.find((c) => String(c.id) === String(filtros.clienteId))?.nombre || 'Cliente'}
                <span className="da-chip-rem__x" aria-hidden>
                  ×
                </span>
              </button>
            )}
            {(filtros.montoMin || filtros.montoMax) && (
              <button
                type="button"
                className="da-chip-rem"
                onClick={() =>
                  setFiltros((prev) => ({ ...prev, montoMin: '', montoMax: '' }))
                }
              >
                Monto {filtros.montoMin || '—'} … {filtros.montoMax || '—'}
                <span className="da-chip-rem__x" aria-hidden>
                  ×
                </span>
              </button>
            )}
            {filtros.estadoPago?.map((estado) => (
              <button
                key={`p-${estado}`}
                type="button"
                className="da-chip-rem"
                onClick={() => handleMultiSelectChange('estadoPago', estado)}
              >
                Pago: {estado}
                <span className="da-chip-rem__x" aria-hidden>
                  ×
                </span>
              </button>
            ))}
            {filtros.estadoExpediente?.map((estado) => (
              <button
                key={`e-${estado}`}
                type="button"
                className="da-chip-rem"
                onClick={() => handleMultiSelectChange('estadoExpediente', estado)}
              >
                {estado}
                <span className="da-chip-rem__x" aria-hidden>
                  ×
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FiltrosAvanzados;
